import { ReactNode, useEffect } from 'react';
import { setGlobalEnv } from './000_globals';
import { sendError } from './sentry';

/** По тестированию и работе динамического конфигурирования есть раздел в README */
export function DynamicConfig({ children }: { children: ReactNode }) {
  useEffect(() => {
    void fetchAndUseDynamicConfiguration();
  }, []);

  return children;
}

type DynamicEnv = Record<string, { value: string; since: string; until: string; description: string }>;

export async function fetchAndUseDynamicConfiguration() {
  try {
    if (!window.APP_DYNAMIC_CONFIG_URL && !window.APP_DYNAMIC_CONFIG_FALLBACK_URL) return;

    try {
      const script = document.currentScript;
      if (script && 'src' in script && typeof script.src === 'string' && window.APP_DYNAMIC_CONFIG_FALLBACK_URL) {
        const currentScriptUrl = new URL(script.src);
        const configFallbackUrl = new URL(window.APP_DYNAMIC_CONFIG_FALLBACK_URL);
        if (currentScriptUrl.host === configFallbackUrl.host) {
          window.APP_DYNAMIC_CONFIG_FALLBACK_URL = window.APP_DYNAMIC_CONFIG_URL;
          window.APP_DYNAMIC_CONFIG_URL = configFallbackUrl.toString();
        }
      }
    } catch {
      /* empty */
    }

    const dynamicConfig = await fetchConfigWithFallbackAndTimeout(
      window.APP_DYNAMIC_CONFIG_URL,
      window.APP_DYNAMIC_CONFIG_FALLBACK_URL,
      window.APP_DYNAMIC_CONFIG_TIMEOUT_MS,
    );

    if (!dynamicConfig) return;

    applyDynamicConfiguration(dynamicConfig);
  } catch (error) {
    sendError(new Error('Failed to fetch dynamic env'), {
      actualError: error,
      severity: 'MAJOR',
    });
  }
}
function delayPromise(timeoutMs: number): Promise<undefined> {
  return new Promise(resolve => setTimeout(resolve, timeoutMs));
}

function applyDynamicConfiguration(dynamicConfig: DynamicEnv) {
  function get(envPropertyName: string) {
    return dynamicConfig?.[envPropertyName]?.value;
  }

  setGlobalEnv({
    APP_DYNAMIC_CONFIG_URL: undefined,
    APP_DYNAMIC_CONFIG_FALLBACK_URL: undefined,
    APP_DYNAMIC_CONFIG_TIMEOUT_MS: undefined,
    APP_EXTERNAL_LOGIC_RULES_API_URL: get('APP_EXTERNAL_LOGIC_RULES_API_URL'),
    APP_POLL_CONTEXT_URL: get('APP_POLL_CONTEXT_URL'),
    APP_FILE_UPLOAD_V1_URL: get('APP_FILE_UPLOAD_V1_URL'),
    APP_FILE_UPLOAD_V2_URL: get('APP_FILE_UPLOAD_V2_URL'),
    APP_IC_CONTEXT_URL: get('APP_IC_CONTEXT_URL'),
    APP_POLL_SERVER_REDUCER: get('APP_POLL_SERVER_REDUCER'),
    APP_TEST_AUTH_DISABLED: pipe(get('APP_TEST_AUTH_DISABLED'), v => v === 'true'),
    APP_TWORK_REDIRECT_URL: get('APP_TWORK_REDIRECT_URL'),
    APP_TWORK_LOGIN_URL: get('APP_TWORK_LOGIN_URL'),
    APP_TWORK_CLIENT_ID: get('APP_TWORK_CLIENT_ID'),
    POLL_STORAGE_UPDATE_TIMEOUT: get('POLL_STORAGE_UPDATE_TIMEOUT'),
    POLL_ERROR_TEXT: get('POLL_ERROR_TEXT'),
    POLL_ERROR_URL: get('POLL_ERROR_URL'),
    APP_SENTRY_DSN: get('APP_SENTRY_DSN'),
  });
}

function pipe<T>(v: string | undefined, transform: (i: string) => T): T | undefined {
  return v === undefined ? undefined : transform(v);
}
async function fetchConfigWithFallbackAndTimeout(primaryUrl: string, fallbackUrl?: string, timeoutMs: number = 30000) {
  const fetchWithFallback = async () => {
    try {
      return await fetchAsJson<DynamicEnv>(primaryUrl);
    } catch {
      if (fallbackUrl) {
        return await fetchAsJson<DynamicEnv>(fallbackUrl);
      }
      return undefined;
    }
  };

  return Promise.race([fetchWithFallback(), delayPromise(timeoutMs)]);
}

function fetchAsJson<T>(url: string) {
  return fetch(url).then(v => v.json() as Promise<T>);
}
